import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  CreditCard, Zap, Check, ExternalLink, AlertCircle,
  RefreshCw, Lock, XCircle,
} from 'lucide-react'
import { Button } from '@brix/ui'
import { useAuthStore } from '@/store/authStore'
import { supabase } from '@/infrastructure/supabase/client'
import { PaymentModal } from '@/components/billing/PaymentModal'
import { BillingProfileForm } from '@/components/billing/BillingProfileForm'
import { InvoiceList } from '@/components/billing/InvoiceList'

const SUPABASE_URL = import.meta.env['VITE_SUPABASE_URL'] as string

type Interval = 'month' | 'year'
type Currency = 'usd' | 'mxn'
type PortalFlow = 'payment_method_update' | 'subscription_cancel' | 'subscription_update'

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    features: ['1 site', '5 pages per site', '5 blog posts per site', '21 block types', 'Theme (fonts & colors)', 'Community support'],
    usd: { monthly: { label: '$0', sub: 'forever' }, annual: { label: '$0', sub: 'forever' } },
    mxn: { monthly: { label: '$0', sub: 'forever' }, annual: { label: '$0', sub: 'forever' } },
  },
  {
    id: 'starter',
    name: 'Starter',
    features: ['5 sites', 'Unlimited pages', 'Unlimited blog posts', 'Analytics dashboard', 'Custom domains', 'Email support'],
    stripePlan: 'starter',
    usd: { monthly: { label: '$9', sub: '/mo' }, annual: { label: '$7', sub: '/mo · $84/yr' }, monthlyPrice: '$9' },
    mxn: { monthly: { label: '$169', sub: '/mo' }, annual: { label: '$125', sub: '/mo · $1,499/yr' }, monthlyPrice: '$169' },
  },
  {
    id: 'pro',
    name: 'Pro',
    features: ['Unlimited sites', 'Unlimited pages & posts', 'Analytics dashboard', 'AI Content API', 'Custom domains', 'Priority support'],
    stripePlan: 'pro',
    usd: { monthly: { label: '$29', sub: '/mo' }, annual: { label: '$23', sub: '/mo · $276/yr' }, monthlyPrice: '$29' },
    mxn: { monthly: { label: '$499', sub: '/mo' }, annual: { label: '$400', sub: '/mo · $4,799/yr' }, monthlyPrice: '$499' },
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    features: ['Everything in Pro', 'Analytics dashboard', 'White-label branding', 'Custom SLA', 'Dedicated account manager', 'SSO / SAML'],
    contactSales: true,
    usd: { monthly: { label: 'Custom', sub: '' }, annual: { label: 'Custom', sub: '' } },
    mxn: { monthly: { label: 'Custom', sub: '' }, annual: { label: 'Custom', sub: '' } },
  },
]

interface PaymentModalState {
  clientSecret: string
  planName: string
  price: string
  interval: Interval
  currency: Currency
}

export function BillingPage() {
  const { tenant } = useAuthStore()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [subscription, setSubscription] = useState<{
    plan: string; status: string
    currentPeriodEnd: string | null; cancelAtPeriodEnd: boolean
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [interval, setInterval] = useState<Interval>('month')
  const [currency, setCurrency] = useState<Currency>('usd')
  const [upgrading, setUpgrading] = useState<string | null>(null)
  const [portalLoading, setPortalLoading] = useState<PortalFlow | 'general' | null>(null)
  const [paymentModal, setPaymentModal] = useState<PaymentModalState | null>(null)
  const [confirmCancel, setConfirmCancel] = useState(false)

  const success = searchParams.get('success') === '1'

  const fetchSubscription = async (tenantId: string) => {
    const { data } = await supabase
      .from('subscriptions')
      .select('plan, status, current_period_end, cancel_at_period_end')
      .eq('tenant_id', tenantId)
      .maybeSingle()
    if (data) {
      setSubscription({
        plan: data['plan'] as string,
        status: data['status'] as string,
        currentPeriodEnd: data['current_period_end'] as string | null,
        cancelAtPeriodEnd: data['cancel_at_period_end'] as boolean,
      })
    }
    setLoading(false)
  }

  useEffect(() => {
    if (!tenant?.id) return
    void fetchSubscription(tenant.id)

    const channel = supabase
      .channel(`subscription:${tenant.id}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'subscriptions', filter: `tenant_id=eq.${tenant.id}` },
        () => { void fetchSubscription(tenant.id) },
      )
      .subscribe()

    return () => { void supabase.removeChannel(channel) }
  }, [tenant?.id])

  // Custom-plan tenants are assigned by a platform admin (set_tenant_custom_plan),
  // not through Stripe — they have no row in `subscriptions`, so `tenant.plan`
  // (not the subscriptions-table read above) is the source of truth here.
  const isCustomPlan = tenant?.plan === 'custom'
  const currentPlan = isCustomPlan ? 'custom' : (subscription?.plan ?? 'free')
  const isPaid = currentPlan !== 'free' && !isCustomPlan

  const openPortal = async (flow?: PortalFlow) => {
    const key = flow ?? 'general'
    setPortalLoading(key)
    setConfirmCancel(false)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${SUPABASE_URL}/functions/v1/stripe-portal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(flow ? { flow } : {}),
      })
      const { url } = await res.json() as { url: string }
      if (url) window.open(url, '_blank')
    } catch {
      alert('Could not open the portal. Please try again.')
    } finally {
      setPortalLoading(null)
    }
  }

  const handleUpgrade = async (stripePlan: string) => {
    setUpgrading(stripePlan)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${SUPABASE_URL}/functions/v1/stripe-create-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ plan: stripePlan, interval, currency }),
      })
      if (!res.ok) {
        const err = await res.json() as { error?: string }
        throw new Error(err.error ?? `Error ${res.status}`)
      }
      const { clientSecret } = await res.json() as { clientSecret: string }
      const planData = PLANS.find((p) => p.stripePlan === stripePlan)!
      const pricing = planData[currency][interval === 'month' ? 'monthly' : 'annual']
      setPaymentModal({ clientSecret, planName: planData.name, price: pricing.label, interval, currency })
    } catch (e) {
      alert(`Could not start payment: ${e instanceof Error ? e.message : 'please try again'}`)
    } finally {
      setUpgrading(null)
    }
  }

  const symbol = currency === 'mxn' ? 'MXN' : 'USD'

  return (
    <>
    <div className="max-w-4xl mx-auto py-10 px-6 space-y-10">

      {/* Header */}
      <div className="flex items-center gap-3">
        <CreditCard className="h-6 w-6 text-violet-600" />
        <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
      </div>

      {success && (
        <div className="flex items-center gap-3 rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
          <Check className="h-5 w-5 text-green-500 shrink-0" />
          Your subscription has been updated successfully.
        </div>
      )}

      {/* ── Sección 1: Plan actual ─────────────────────────────── */}
      {!loading && (
        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Your plan</h2>
          <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">

            {/* Plan status row */}
            <div className="flex items-center justify-between px-5 py-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-gray-900 capitalize">{currentPlan}</span>
                  {subscription?.status === 'past_due' && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                      Past due
                    </span>
                  )}
                  {subscription?.cancelAtPeriodEnd && (
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                      Cancels at period end
                    </span>
                  )}
                </div>
                {subscription?.currentPeriodEnd && (
                  <p className="text-sm text-gray-400 mt-0.5">
                    {subscription.cancelAtPeriodEnd ? 'Access until' : 'Renews on'}{' '}
                    {new Date(subscription.currentPeriodEnd).toLocaleDateString(undefined, {
                      year: 'numeric', month: 'long', day: 'numeric',
                    })}
                  </p>
                )}
              </div>
              {isPaid && (
                <button
                  onClick={() => { void openPortal() }}
                  disabled={portalLoading === 'general'}
                  className="flex items-center gap-1.5 text-sm text-violet-600 hover:text-violet-800 font-medium disabled:opacity-50"
                >
                  <ExternalLink className="h-4 w-4" />
                  {portalLoading === 'general' ? 'Opening…' : 'Stripe portal'}
                </button>
              )}
            </div>

            {/* Subscription actions — only for paid plans */}
            {isPaid && (
              <div className="px-5 py-4 flex flex-wrap gap-3">
                <button
                  onClick={() => { void openPortal('payment_method_update') }}
                  disabled={portalLoading === 'payment_method_update'}
                  className="flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <Lock className="h-4 w-4 text-gray-400" />
                  {portalLoading === 'payment_method_update' ? 'Opening…' : 'Update payment method'}
                </button>

                <button
                  onClick={() => { void openPortal('subscription_update') }}
                  disabled={portalLoading === 'subscription_update'}
                  className="flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className="h-4 w-4 text-gray-400" />
                  {portalLoading === 'subscription_update' ? 'Opening…' : 'Change plan'}
                </button>

                {!subscription?.cancelAtPeriodEnd && (
                  <button
                    onClick={() => setConfirmCancel(true)}
                    className="flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <XCircle className="h-4 w-4" />
                    Cancel subscription
                  </button>
                )}
              </div>
            )}

            {/* Cancel confirmation */}
            {confirmCancel && (
              <div className="px-5 py-4 bg-red-50 flex items-center justify-between gap-4">
                <p className="text-sm text-red-700">
                  The Stripe portal will open to confirm cancellation. Your plan stays active until the end of the current period.
                </p>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setConfirmCancel(false)} className="text-sm text-gray-500 hover:text-gray-700">
                    Go back
                  </button>
                  <button
                    onClick={() => { void openPortal('subscription_cancel') }}
                    disabled={portalLoading === 'subscription_cancel'}
                    className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    {portalLoading === 'subscription_cancel' ? 'Opening…' : 'Continue with cancellation'}
                  </button>
                </div>
              </div>
            )}

            {/* past_due warning */}
            {subscription?.status === 'past_due' && (
              <div className="px-5 py-3 bg-amber-50 flex items-center gap-3">
                <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" />
                <p className="text-sm text-amber-700">
                  Your last payment failed. Update your payment method to avoid losing access.
                </p>
                <button
                  onClick={() => { void openPortal('payment_method_update') }}
                  className="ml-auto shrink-0 text-sm font-medium text-amber-700 underline"
                >
                  Update now
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Sección 2: Upgrade ────────────────────────────────── */}
      {isCustomPlan ? (
        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Your custom plan</h2>
          <div className="rounded-2xl border border-violet-200 bg-violet-50 p-6">
            <p className="text-sm text-gray-700 mb-4">
              Your workspace is on a negotiated custom plan, set up by the Brix team. It isn't managed through
              self-service checkout — reach out if you need to change it.
            </p>
            {tenant?.customLimits && (
              <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5 text-sm text-gray-700">
                <li><span className="font-semibold">{tenant.customLimits.siteLimit}</span> sites</li>
                <li><span className="font-semibold">{tenant.customLimits.pageLimit}</span> pages/site</li>
                <li><span className="font-semibold">{tenant.customLimits.postLimit}</span> posts/site</li>
                <li>Analytics: <span className="font-semibold">{tenant.customLimits.canViewAnalytics ? 'Yes' : 'No'}</span></li>
                <li>AI assistant: <span className="font-semibold">{tenant.customLimits.canUseAiAssistant ? 'Yes' : 'No'}</span></li>
                <li>Pro blocks: <span className="font-semibold">{tenant.customLimits.canUseProBlocks ? 'Yes' : 'No'}</span></li>
              </ul>
            )}
            <a
              href="mailto:sales@brix.app?subject=Custom plan"
              className="inline-flex items-center gap-2 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-700 transition-colors"
            >
              Talk to us
            </a>
          </div>
        </section>
      ) : (
      <section>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          {isPaid ? 'Change plan' : 'Choose a plan'}
        </h2>

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 p-1">
            {(['month', 'year'] as Interval[]).map((v) => (
              <button key={v} onClick={() => setInterval(v)}
                className={['rounded-full px-5 py-1.5 text-sm font-medium transition-all',
                  interval === v ? 'bg-violet-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900',
                ].join(' ')}>
                {v === 'month' ? 'Monthly' : 'Annual'}
                {v === 'year' && <span className="ml-1.5 rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-bold text-green-700">−22%</span>}
              </button>
            ))}
          </div>
          <div className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 p-1">
            {(['usd', 'mxn'] as Currency[]).map((c) => (
              <button key={c} onClick={() => setCurrency(c)}
                className={['rounded-full px-4 py-1.5 text-sm font-medium transition-all uppercase',
                  currency === c ? 'bg-violet-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900',
                ].join(' ')}>{c}</button>
            ))}
          </div>
          {interval === 'year' && <p className="text-sm text-green-600 font-medium">Save up to 2 months</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {PLANS.map((plan) => {
            const isCurrent = currentPlan === plan.id
            const canUpgrade = !isCurrent && 'stripePlan' in plan && !!plan.stripePlan
            const isEnterprise = 'contactSales' in plan && plan.contactSales
            const pricing = plan[currency as 'usd' | 'mxn'][interval === 'month' ? 'monthly' : 'annual']
            return (
              <div key={plan.id} className={[
                'rounded-2xl border p-6 flex flex-col transition-all',
                isCurrent ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-500/30' : 'border-gray-200 bg-white hover:border-violet-200',
              ].join(' ')}>
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-gray-600">{plan.name}</p>
                    {isCurrent && <span className="rounded-full bg-violet-600 px-2 py-0.5 text-[10px] font-bold text-white uppercase tracking-wide">Current</span>}
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className={`font-extrabold text-gray-900 ${isEnterprise ? 'text-xl' : 'text-3xl'}`}>{pricing.label}</span>
                    {pricing.sub && <span className="text-sm text-gray-400">{pricing.sub}</span>}
                  </div>
                  {'monthlyPrice' in plan[currency as 'usd' | 'mxn'] && interval === 'year' && (
                    <p className="mt-0.5 text-xs text-gray-400">
                      <span className="line-through">{(plan[currency as 'usd' | 'mxn'] as { monthlyPrice: string }).monthlyPrice}/mo</span>{' '}with annual plan
                    </p>
                  )}
                  {'stripePlan' in plan && plan.stripePlan && <p className="mt-1 text-[10px] text-gray-400 font-medium">{symbol}</p>}
                </div>
                <ul className="space-y-2 flex-1 mb-6">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-violet-500 shrink-0" />
                      <span>{f}</span>
                      {f === 'Custom domains' && (
                        <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-400">
                          soon
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                {isEnterprise ? (
                  <a
                    href="mailto:sales@brix.app?subject=Enterprise plan"
                    className="w-full flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-medium bg-gray-900 text-white hover:bg-gray-700 transition-colors"
                  >
                    Talk to us
                  </a>
                ) : canUpgrade ? (
                  <Button onClick={() => handleUpgrade((plan as { stripePlan: string }).stripePlan)} disabled={upgrading === (plan as { stripePlan?: string }).stripePlan}
                    className="w-full flex items-center justify-center gap-2">
                    <Zap className="h-4 w-4" />
                    {upgrading === (plan as { stripePlan?: string }).stripePlan ? 'One moment…' : `Upgrade to ${plan.name}`}
                  </Button>
                ) : (
                  <div className={['rounded-xl py-2.5 text-center text-sm font-medium',
                    isCurrent ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-400'].join(' ')}>
                    {isCurrent ? 'Your current plan' : 'Free forever'}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </section>
      )}

      {/* ── Sección 3: Datos de facturación ───────────────────── */}
      <section>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Billing details</h2>
        <BillingProfileForm />
      </section>

      {/* ── Sección 4: Historial de pagos ─────────────────────── */}
      <section>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Payment history</h2>
        <InvoiceList />
      </section>

    </div>

    {paymentModal && (
      <PaymentModal
        clientSecret={paymentModal.clientSecret}
        plan={{ name: paymentModal.planName, price: paymentModal.price, interval: paymentModal.interval, currency: paymentModal.currency }}
        onSuccess={() => { setPaymentModal(null); void navigate('/billing?success=1') }}
        onClose={() => setPaymentModal(null)}
      />
    )}
    </>
  )
}
